import { useRef, useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Box, TextField, Divider, Snackbar, Alert } from '@mui/material';
import { useGetPersonalFields } from '../../../../hooks/Marketing';
import InsertBarcodeDialog, { BarcodeResult } from './InsertBarcodeDialog';
import InsertPersonalFieldsDialog from './InsertPersonalFieldsDialog';
import HtmlPreviewDialog from './HtmlPreviewDialog';
import BatPreviewDialog from './BatPreviewDialog';
import EmailEditorToolbar from './creation/email/EmailEditorToolbar';
import HtmlCodeEditor, { HtmlCodeEditorHandle } from './creation/email/HtmlCodeEditor';
import UnlayerEditorPane from './creation/email/UnlayerEditorPane';
import useUnlayerEditor from './creation/email/useUnlayerEditor';
import { InsertedBlock, buildMergeTags } from './creation/email/unlayerDesign';
import { notifyOfferTokenInserted } from './creation/shared/offerTokens';

export type EmailEditorMode = 'unlayer' | 'html';

export interface EmailContent {
  mode: EmailEditorMode;
  html: string;
  design?: object;
  subject: string;
}

export interface EmailUnlayerInitialContent {
  mode?: EmailEditorMode;
  html?: string;
  design?: object;
  subject?: string;
}

export interface EmailUnlayerHandle {
  getContent: () => Promise<EmailContent | null>;
}

interface EmailUnlayerSectionProps {
  onSave?: () => void;
  isLandingPage?: boolean;
  offerCount?: number;
  offerTitles?: string[];
  onAddOffer?: () => void;
  onOfferUsed?: (offerIndex: number) => void;
  onOfferIndicesChange?: (indices: Set<number>) => void;
  initialContent?: EmailUnlayerInitialContent | null;
}

const SAVED_FEEDBACK_MS = 2500;

const EmailUnlayerSection = forwardRef<EmailUnlayerHandle, EmailUnlayerSectionProps>((props, ref) => {
  const { onSave, isLandingPage = false, offerCount = 1, offerTitles, onAddOffer, onOfferUsed, onOfferIndicesChange, initialContent } = props;
  const { data: personalFieldCategories } = useGetPersonalFields('EMAILING');
  const mergeTags = useMemo(() => buildMergeTags(offerCount, personalFieldCategories), [offerCount, personalFieldCategories]);
  const unlayer = useUnlayerEditor({
    initialDesign: initialContent?.design,
    initialHtml: initialContent?.html,
    mergeTags,
    onOfferIndicesChange,
  });
  const htmlEditorRef = useRef<HtmlCodeEditorHandle>(null);

  const [subject, setSubject] = useState(() => initialContent?.subject ?? '');
  const [editorMode, setEditorMode] = useState<EmailEditorMode>(() => (initialContent?.mode === 'html' ? 'html' : 'unlayer'));
  const [htmlContent, setHtmlContent] = useState(() => (initialContent?.mode === 'html' ? initialContent.html ?? '' : ''));
  const [saved, setSaved] = useState(false);
  const [barcodeOpen, setBarcodeOpen] = useState(false);
  const [personalFieldsOpen, setPersonalFieldsOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [preview, setPreview] = useState({ open: false, html: '' });
  const [bat, setBat] = useState<{ open: boolean; content: EmailContent | null }>({ open: false, content: null });

  const isHtmlMode = editorMode === 'html';
  const closeSnackbar = () => setSnackbar(s => ({ ...s, open: false }));

  const { getCachedContent } = unlayer;
  useImperativeHandle(ref, () => ({
    getContent: async () => {
      if (isHtmlMode) return { mode: 'html', html: htmlContent, subject };
      const content = await getCachedContent();
      return content ? { mode: 'unlayer', ...content, subject } : null;
    },
  }), [isHtmlMode, htmlContent, subject, getCachedContent]);

  const insertBlock = async (block: InsertedBlock, message: string) => {
    if (await unlayer.insertBlock(block)) setSnackbar({ open: true, message });
  };

  const handleInsertPersonalField = (anchor: string) => {
    if (isHtmlMode) htmlEditorRef.current?.insertAtCursor(anchor);
    else insertBlock({ kind: 'text', text: anchor }, "Bloc texte ajouté dans l'email");
  };

  const handleBarcodeConfirm = ({ token, snippet, image }: BarcodeResult) => {
    if (isHtmlMode) htmlEditorRef.current?.insertAtCursor(snippet);
    else if (image) insertBlock({ kind: 'image', alt: token, ...image }, `Bloc image ajouté dans l'email (${token})`);
    else insertBlock({ kind: 'text', text: token }, "Bloc texte ajouté dans l'email");
    notifyOfferTokenInserted(token, { offerCount, onOfferUsed, onAddOffer });
  };

  const exportCurrentHtml = async () => (isHtmlMode ? htmlContent : (await unlayer.exportContent())?.html ?? null);

  const handleShowPreview = async () => {
    const html = await exportCurrentHtml();
    if (html !== null) setPreview({ open: true, html });
  };

  const handleOpenBat = async () => {
    const html = await exportCurrentHtml();
    if (html !== null) setBat({ open: true, content: { mode: editorMode, html, subject } });
  };

  const handleSave = async () => {
    if (!(await unlayer.exportAndCache())) return;
    onSave?.();
    setSaved(true);
    setTimeout(() => setSaved(false), SAVED_FEEDBACK_MS);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <EmailEditorToolbar
        mode={editorMode}
        ready={unlayer.ready}
        hasHtmlContent={htmlContent.trim() !== ''}
        saved={saved}
        onInsertCode={() => setBarcodeOpen(true)}
        onInsertField={() => setPersonalFieldsOpen(true)}
        onPreview={handleShowPreview}
        onBat={handleOpenBat}
        onSave={handleSave}
        onToggleMode={() => setEditorMode(isHtmlMode ? 'unlayer' : 'html')}
      />

      {!isLandingPage && (
        <>
          <TextField
            label={`Objet (${subject.length}/75 caractères)`}
            value={subject}
            onChange={e => setSubject(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 75 } }}
            fullWidth
            size="small"
            sx={{ mb: 2, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' }, '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' } }}
          />
          <Divider sx={{ mb: 2 }} />
        </>
      )}

      {isHtmlMode && <HtmlCodeEditor ref={htmlEditorRef} value={htmlContent} onChange={setHtmlContent} />}

      <UnlayerEditorPane editorRef={unlayer.editorRef} visible={!isHtmlMode} ready={unlayer.ready} onReady={unlayer.handleReady} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={closeSnackbar} sx={{ fontSize: '0.82rem' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <InsertBarcodeDialog
        open={barcodeOpen}
        onClose={() => setBarcodeOpen(false)}
        onConfirm={handleBarcodeConfirm}
        offerCount={offerCount}
        offerTitles={offerTitles}
      />
      <InsertPersonalFieldsDialog
        open={personalFieldsOpen}
        onClose={() => setPersonalFieldsOpen(false)}
        onInsert={handleInsertPersonalField}
        channel="email"
      />
      <HtmlPreviewDialog open={preview.open} onClose={() => setPreview(p => ({ ...p, open: false }))} htmlContent={preview.html} />
      <BatPreviewDialog open={bat.open} onClose={() => setBat(b => ({ ...b, open: false }))} content={bat.content} />
    </Box>
  );
});

EmailUnlayerSection.displayName = 'EmailUnlayerSection';

export default EmailUnlayerSection;
